import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface FilterDropdownProps {
  label: string;
  options: string[];
  value: string | string[];
  onChange: (value: string | string[]) => void;
  isMulti?: boolean;
  displayAllText?: string;
  formatOptionLabel?: (option: string) => string | JSX.Element;
  icon?: React.ReactNode;
}

const FilterDropdown = ({ 
  label, 
  options, 
  value, 
  onChange, 
  isMulti = false,
  displayAllText,
  formatOptionLabel,
  icon
}: FilterDropdownProps) => {
  const handleValueChange = (newValue: string) => {
    if (!isMulti) {
      onChange(newValue);
      return;
    }

    const currentValues = Array.isArray(value) ? value : [];
    if (newValue === 'All') {
      onChange(['All']);
    } else if (currentValues.includes(newValue)) {
      const filtered = currentValues.filter(v => v !== newValue);
      onChange(filtered.length === 0 ? ['All'] : filtered);
    } else {
      const newValues = currentValues.filter(v => v !== 'All').concat(newValue);
      onChange(newValues);
    }
  };

  const displayValue = () => {
    if (!isMulti) return formatOptionLabel ? formatOptionLabel(value as string) : value;
    const values = Array.isArray(value) ? value : [];
    if (values.includes('All')) {
      if (label === "Employees") return "All Employees";
      return displayAllText || "All Categories";
    }
    if (values.length === 1 && formatOptionLabel) {
      return formatOptionLabel(values[0]);
    }
    return `${values.length} selected`;
  };

  // Filter out any empty strings from options and sort them
  const sortOptions = (opts: string[]) => {
    const validOpts = opts.filter(option => option.trim() !== '');
    
    if (label === "Employees") {
      // Custom sorting for employee ranges
      return validOpts.sort((a, b) => {
        if (a === 'All') return -1;
        if (b === 'All') return 1;
        
        // Extract the first number from each range
        const getFirstNumber = (str: string) => {
          const match = str.match(/\d+/);
          return match ? parseInt(match[0]) : 0;
        };
        
        return getFirstNumber(a) - getFirstNumber(b);
      });
    }
    
    if (label === "Modality") {
      // Custom sorting for modality to ensure "None" is last
      return validOpts.sort((a, b) => {
        if (a === 'All') return -1;
        if (b === 'All') return 1;
        if (a === 'None') return 1;
        if (b === 'None') return -1;
        return a.localeCompare(b);
      });
    }
    
    if (label === "Discounts") {
      // Keep discounts in their defined order (All first, then as defined)
      return validOpts;
    }
    
    // Regular alphabetical sorting for other filters
    return validOpts.sort((a, b) => {
      if (a === 'All') return -1;
      if (b === 'All') return 1;
      return a.localeCompare(b);
    });
  };

  const sortedOptions = sortOptions(options);

  return (
    <div className="w-[200px]">
      <Select 
        value={Array.isArray(value) ? value[0] : value} 
        onValueChange={handleValueChange}
      >
        <SelectTrigger className="w-full bg-white !rounded-none">
          <div className="flex items-center gap-2">
            {icon}
            <SelectValue placeholder={label}>
              {displayValue()}
            </SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {sortedOptions.map((option) => (
            <SelectItem 
              key={option} 
              value={option || 'default'}
              className={isMulti && Array.isArray(value) && value.includes(option) ? "bg-accent" : ""}
            >
              {formatOptionLabel ? formatOptionLabel(option) : option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default FilterDropdown;